import Vue from 'vue'
import Router from 'vue-router'

import coreApi from '@/api/core.api'
import HelpCenter from '@/components/ayuda/HelpCenter.vue'
import ClientHistory from '@/components/client-history/ClientHistory.vue'
import Dashboard from '@/components/Dashboard.vue'
import DealerBikeDetail from '@/components/dealer-bike/Detail.vue'
import DealerBikes from '@/components/dealer-bike/index.vue'
import DealerBikesList from '@/components/dealer-bike/List.vue'
import DealerBikesLoan from '@/components/dealer-bike/Loan.vue'
import LoansList from '@/components/dealer-bike/LoansList.vue'
import DealerBikesRegistration from '@/components/dealer-bike/Registration.vue'
import ReturnDealerBike from '@/components/dealer-bike/ReturnDealerBike.vue'
import HomeDashboard from '@/components/HomeDashboard.vue'
import DataImport from '@/components/import/DataImport.vue'
import LeadDetail from '@/components/leads/LeadDetail.vue'
import Leads from '@/components/leads/Leads.vue'
import Login from '@/components/Login.vue'
import NoDealerId from '@/components/NoDealerId.vue'
import CreateOffer from '@/components/offer-manager/CreateOffer.vue'
import CreateOfferMotorbike from '@/components/offer-manager/CreateOfferMotorbike.vue'
import InitOfferClientHistory from '@/components/offer-manager/InitOfferClientHistory.vue'
import Offers from '@/components/offer-manager/Offers.vue'
import ResumeOfferMotorbike from '@/components/offer-manager/ResumeOfferMotorbike.vue'
import ContractHondaMapit from '@/components/other/ContractHondaMapit.vue'
import RoutesCheck from '@/components/other/RoutesCheck.vue'
import HondaRegistration from '@/components/registrations/HondaRegistration/HondaRegistration.vue'
import Registrations from '@/components/registrations/HondaRegistration/Registrations.vue'
import TransferredVehicles from '@/components/registrations/MapitWizard/TransferredVehicles.vue'
import RegistrationMapitConnect from '@/components/registrations/RegistrationMapitConnect.vue'
import RegistrationOnlyMapit from '@/components/registrations/RegistrationOnlyMapit.vue'
import Repairs from '@/components/repairs/Repairs.vue'
import RepairShopProfile from '@/components/repairshop/RepairShopProfile.vue'
import Revisions from '@/components/revisions/Revisions.vue'
import Solicitudes from '@/components/solicitudes/Requests.vue'
import StocksManager from '@/components/stocks-manager/StocksManager.vue'
import UsersManager from '@/components/users/manager/UsersManager.vue'
import User from '@/components/users/User.vue'
import Users from '@/components/users/Users.vue'
import Warranties from '@/components/warranties/Warranties.vue'
import {identifyUser} from '@/config/amplitude'
import i18n from '@/i18n'
import {eventBus} from '@/main'
import {authAdapter} from '@/services/auth-adapter'
import {
  clearAllAuthData,
  clearSpecificIdentity,
  extractIdentityIdFromError,
  isIdentityForbiddenError,
} from '@/services/auth-cleanup'
import {getInactivityTracker} from '@/services/inactivity-tracker'
import {hasAccess} from '@/utils/auth-helper'
import {isDevelopment} from '@/utils/env'
import {Permissions} from '@/utils/permission-helper'
import {createCompatibleUser} from '@/utils/user-compat'
import {checkAppVersion} from '@/utils/versions'

import store from '../vuex/index'

// Default locale for GTM tracking (Google Tag Manager requires a fixed locale)
const GTM_LOCALE = 'es'

const SalesAppointmentsMF = () => import('@/components/sales-appointments/SalesAppointments.vue')

Vue.use(Router)

const scrollBehavior = (to: {hash: any}) => {
  if (to.hash) {
    return {
      selector: to.hash,
    }
  }
}

export const refreshSession = async () => {
  try {
    // Check if user is authenticated using our unified adapter
    const isAuthenticated = await authAdapter.isAuthenticated()
    if (!isAuthenticated) {
      console.log('🔍 Router guard - User not authenticated')
      return null
    }

    // Get user attributes using our unified adapter (handles both OIDC and User Pool)
    const userAttributes = await authAdapter.fetchUserAttributes()

    // Get current session for backwards compatibility
    const session = await authAdapter.getCurrentSession()

    // Create a v3-compatible user object for legacy code
    const compatibleUser = createCompatibleUser(userAttributes, session)

    return compatibleUser
  } catch (err: any) {
    // Special handling for identity forbidden error
    if (isIdentityForbiddenError(err)) {
      // Extract and clear specific identity if found in error message
      const identityId = extractIdentityIdFromError(err.message)
      if (identityId) {
        clearSpecificIdentity(identityId)
      }

      // Clear all authentication data
      clearAllAuthData()

      // Dispatch logout
      store.dispatch('logout')

      // Show user-friendly message
      store.dispatch('setAlertResult', {
        type: 'warning',
        stringId: i18n.t('auth.sessionExpired'),
        open: true,
        timeout: -1,
      })

      // Return null to prevent further processing
      return null
    }

    // Don't log errors for expected unauthenticated states
    if (err.name === 'UserUnAuthenticatedException') {
      console.log('🔍 Router guard - No valid session found')
      return null
    }

    console.error('Unable to refresh session', err)
    if ((err && err.name === 'NotAuthorizedException') || err.name === 'PasswordResetRequiredException') {
      // Clear identity data before logout for NotAuthorizedException
      clearAllAuthData()
      store.dispatch('logout')
    }
    throw err
  }
}

const getUser = async () => {
  try {
    const cognitoUser: any = await refreshSession()

    if (!cognitoUser) {
      // User is not authenticated
      store.commit('SET_COGNITO_USER', null)
      store.commit('SET_USER', null)
      return null
    }

    store.commit('SET_COGNITO_USER', cognitoUser)

    if (cognitoUser && cognitoUser.signInUserSession) {
      const accessTokenPayload = cognitoUser.signInUserSession.accessToken.payload
      if (accessTokenPayload['cognito:groups']) {
        if (
          store.getters.getUserGroups &&
          accessTokenPayload['cognito:groups'] &&
          JSON.stringify(store.getters.getUserGroups) !== JSON.stringify(accessTokenPayload['cognito:groups'])
        ) {
          store.commit('SET_USER_GROUPS', accessTokenPayload['cognito:groups'])
        }
      } else {
        store.commit('SET_USER_GROUPS', [])
      }
      const cachedUser = store.getters.getUser
      const freshDealerId = cognitoUser.attributes['custom:dealerId'] || null

      if (cachedUser && cachedUser.dealerId !== freshDealerId) {
        console.log('[Router] dealerId mismatch after refresh — rebuilding user', {
          cached: cachedUser.dealerId,
          fresh: freshDealerId,
        })
      }

      if (cachedUser && cachedUser.dealerId === freshDealerId) {
        return cachedUser
      }
      const userInfo = {
        username: cognitoUser.attributes.email,
        attributes: cognitoUser.attributes,
      }

      // Try to get the dealer info first (this is more critical for NoDealerId detection)
      let dealer = null
      const dealerId = cognitoUser.attributes['custom:dealerId']

      if (dealerId) {
        try {
          const response = await coreApi.getDealer(dealerId)
          dealer = response.data
          console.log('dealer', dealer)
        } catch (dealerError) {
          console.error('Error getting dealer, but allowing login:', dealerError)
        }
      } else {
        console.log('No dealerId in token - user will be redirected to NoDealerId page')
      }

      // Try to get legacy token asynchronously (don't block the user flow)
      // This is only needed for legacy API calls, not for navigation
      coreApi
        .getLegacyToken()
        .then(({data: {legacyToken}}) => {
          store.commit('SET_TOKEN', `Bearer ${legacyToken}`)
          console.log('Legacy token obtained successfully')
        })
        .catch(legacyTokenError => {
          console.error('Error getting legacy token, but allowing login:', legacyTokenError)
        })

      // Identify user with or without dealer
      if (dealer) {
        identifyUser(userInfo.username || userInfo.attributes['email'], {
          ...userInfo.attributes,
          dealer: `${dealer.name} - (${dealer.id})`,
        })
      } else {
        identifyUser(userInfo.username || userInfo.attributes['email'], {
          ...userInfo.attributes,
          dealer: 'No dealer found',
        })
      }
      const user = {
        groupId: dealer?.legacy?.id?.toString(), //TODO: Eliminar dependencia LEGACY
        dealerId: dealer ? userInfo.attributes['custom:dealerId'] : null,
        login: userInfo.attributes['email'],
      }
      store.commit('SET_USER', user)
      return user
    }
  } catch (error) {
    console.error('Error getting user', error)
    store.commit('SET_USER', null)
    return null
  }
}

const permissions = (): Permissions => {
  return store.getters.getPermissions || undefined
}

const router = new Router({
  mode: 'history',
  scrollBehavior,
  routes: [
    {
      path: '/login',
      name: 'Login',
      component: Login,
      beforeEnter: async (to, _from, next) => {
        if (to.query._fromAuthCallback === 'true') {
          return next()
        }

        try {
          const user = await getUser()
          if (user) {
            return next({name: 'Dashboard', query: to.query})
          }
          next()
        } catch (error) {
          next()
        }
      },
      meta: {
        allowUnauthenticated: true,
        title: 'title.login',
        gtm: i18n.t('title.login', GTM_LOCALE),
      },
    },
    {
      path: '/mapit-login',
      name: 'MapitLogin',
      component: Login,
      beforeEnter: async (to, _from, next) => {
        try {
          const user = await getUser()
          if (user) {
            return next({name: 'Dashboard', query: to.query})
          }
          next()
        } catch (error) {
          next()
        }
      },
      meta: {
        allowUnauthenticated: true,
        title: 'title.login',
        gtm: i18n.t('title.login', 'es'),
        forceCognitoOnly: true,
      },
    },
    {
      path: '/auth/callback',
      name: 'AuthCallback',
      redirect: to => {
        // Generate random background image number (1-3) to preserve across redirect
        const bgImage = Math.ceil(Math.random() * 3)

        // Preserve query parameters (code, state, etc.) for Login component to handle
        return {
          name: 'Login',
          query: {
            ...to.query,
            _fromAuthCallback: 'true',
            _bg: bgImage.toString(),
          },
        }
      },
      meta: {
        allowUnauthenticated: true,
        title: 'auth.processingLogin',
      },
    },
    {
      path: '/RoutesCheck',
      name: 'RoutesCheck',
      component: RoutesCheck,
    },
    {
      path: '/contract/:params',
      name: 'ContractHondaMapit',
      props: true,
      component: ContractHondaMapit,
    },
    {
      path: '/',
      component: Dashboard,
      children: [
        {
          path: 'no-dealer-id',
          name: 'NoDealerId',
          component: NoDealerId,
          meta: {
            title: 'no-dealer-id.title',
            gtm: i18n.t('title.no-dealer-id', GTM_LOCALE),
          },
        },
        {
          path: '',
          name: 'Dashboard',
          component: HomeDashboard,
          meta: {
            title: 'title.dashboard',
            gtm: i18n.t('title.dashboard', GTM_LOCALE),
          },
        },
        {
          path: 'registrations/create',
          name: 'HondaRegistration',
          component: HondaRegistration,
          meta: {
            allowed: () => permissions()?.registrations.canSee,
            title: 'title.registrations',
            gtm: i18n.t('title.registrations', GTM_LOCALE),
          },
        },
        {
          path: 'registrations/mapit-only',
          name: 'RegistrationOnlyMapit',
          component: RegistrationOnlyMapit,
          meta: {
            allowed: () => permissions()?.registrations.canSee,
            title: 'title.registrations-mapit',
            gtm: i18n.t('title.registrations-mapit', GTM_LOCALE),
          },
        },
        {
          path: 'registrations/transferred-motorbikes',
          name: 'TransferredVehicles',
          component: TransferredVehicles,
          meta: {
            allowed: () => permissions()?.registrations.canSee,
            title: 'title.transferred-vehicles',
            gtm: i18n.t('title.transferred-vehicles', GTM_LOCALE),
          },
        },
        {
          path: 'connect',
          name: 'RegistrationMapitConnect',
          component: RegistrationMapitConnect,
          meta: {
            allowed: () => permissions()?.registrations.canSee,
            title: 'title.registrations-mapit',
            gtm: i18n.t('title.registrations-mapit', GTM_LOCALE),
          },
        },
        {
          path: 'registrations',
          name: 'Registrations',
          component: Registrations,
          meta: {
            allowed: () => permissions()?.registrations.canSee,
            title: 'title.pending-registrations',
            gtm: i18n.t('title.pending-registrations', GTM_LOCALE),
          },
        },
        {
          path: 'profile',
          name: 'Profile',
          component: RepairShopProfile,
          meta: {
            allowed: () => permissions()?.dealerProfile.canSee,
            title: 'title.edit-account',
            gtm: i18n.t('title.edit-account', GTM_LOCALE),
          },
        },
        {
          path: 'requests',
          name: 'Solicitudes',
          component: Solicitudes,
          meta: {
            allowed: () => permissions()?.requests.canSee,
            title: 'title.create-incidence',
            gtm: i18n.t('title.create-incidence', GTM_LOCALE),
          },
        },
        {
          path: 'appointments',
          name: 'Revisions',
          component: Revisions,
          meta: {
            allowed: () => permissions()?.appointments.canSee,
            title: 'title.revisions',
            gtm: i18n.t('title.revisions', GTM_LOCALE),
          },
        },
        {
          path: 'appointments/:id',
          name: 'RevisionsById',
          component: Revisions,
          meta: {
            allowed: () => permissions()?.appointments.canSee,
            title: 'title.revisions',
            gtm: i18n.t('title.revisions', GTM_LOCALE),
          },
        },
        {
          path: 'repairs',
          name: 'Repairs',
          component: Repairs,
          meta: {
            allowed: () => permissions()?.appointments.canSee,
            title: 'title.repairs',
            gtm: i18n.t('title.repairs', GTM_LOCALE),
          },
        },
        {
          path: 'repairs/:id',
          name: 'RepairsById',
          component: Repairs,
          meta: {
            allowed: () => permissions()?.appointments.canSee,
            title: 'title.repairs',
            gtm: i18n.t('title.repairs', GTM_LOCALE),
          },
        },
        {
          path: 'help',
          name: 'Ayuda',
          component: HelpCenter,
          meta: {
            allowed: () => permissions()?.help.canSee,
            title: 'title.help-center',
            gtm: i18n.t('title.help-center', GTM_LOCALE),
          },
        },
        {
          path: 'users',
          name: 'Users',
          component: Users,
          meta: {
            allowed: () => permissions()?.customers.canSee,
            title: 'title.users',
            gtm: i18n.t('title.users', GTM_LOCALE),
          },
        },
        {
          path: 'user/:userId',
          name: 'User',
          props: true,
          component: User,
          meta: {
            allowed: () => permissions()?.registrations.canSee,
            title: 'title.users',
            gtm: i18n.t('title.users', GTM_LOCALE),
          },
        },
        {
          path: 'users/:id',
          name: 'UsersId',
          props: true,
          component: Users,
          meta: {
            allowed: () => permissions()?.registrations.canSee,
            title: 'title.users',
            gtm: i18n.t('title.users', GTM_LOCALE),
          },
        },
        {
          path: 'import',
          name: 'DataImport',
          component: DataImport,
          meta: {
            allowed: () => permissions()?.imports.canSee,
            title: 'title.import',
            gtm: i18n.t('title.import', GTM_LOCALE),
          },
        },
        {
          path: 'leads',
          name: 'Leads',
          component: Leads,
          meta: {
            allowed: () => permissions()?.leads.canSee,
            title: 'title.leads',
            gtm: i18n.t('title.leads', GTM_LOCALE),
          },
        },
        {
          path: 'leads/:id',
          name: 'LeadDetail',
          component: LeadDetail,
          meta: {
            allowed: () => permissions()?.leads.canSee,
            title: 'title.leads-detail',
            gtm: i18n.t('title.leads-detail', GTM_LOCALE),
          },
        },
        {
          path: 'sales-appointments',
          name: 'SalesAppointments',
          component: SalesAppointmentsMF,
          meta: {
            allowed: () => permissions()?.salesAppointments?.canSee,
            title: 'title.sales-appointments',
            gtm: i18n.t('title.sales-appointments', GTM_LOCALE),
          },
        },
        {
          path: 'sales-appointments/:id',
          name: 'SalesAppointmentDetail',
          component: SalesAppointmentsMF,
          meta: {
            allowed: () => permissions()?.salesAppointments?.canSee,
            title: 'title.sales-appointment-detail',
            gtm: i18n.t('title.sales-appointment-detail', GTM_LOCALE),
          },
        },
        {
          path: 'offer-manager/offers',
          name: 'Offers',
          component: Offers,
          meta: {
            allowed: () => permissions()?.offers.canSee,
            title: 'title.list-offers',
            gtm: i18n.t('title.list-offers', GTM_LOCALE),
          },
        },
        {
          path: 'stocks-manager',
          name: 'StocksManager',
          component: StocksManager,
          meta: {
            allowed: () => permissions()?.stocks.canSee,
            title: 'title.stocks',
            gtm: i18n.t('title.stocks', GTM_LOCALE),
          },
        },
        {
          path: 'users-manager',
          name: 'UsersManager',
          component: UsersManager,
          meta: {
            allowed: () => permissions()?.cognitoUserManagement?.canSee,
            title: 'title.users-manager',
            gtm: i18n.t('title.users-manager', GTM_LOCALE),
          },
        },
        {
          path: 'warranties',
          name: 'Warranties',
          component: Warranties,
          meta: {
            allowed: () => permissions()?.userCredentials.canSee,
            title: 'title.warranties',
            gtm: i18n.t('title.warranties', GTM_LOCALE),
          },
        },
        {
          path: 'offer-manager',
          name: 'CreateOffer',
          component: CreateOffer,
          children: [
            {
              path: 'create',
              name: 'InitOfferClientHistory',
              component: InitOfferClientHistory,
              meta: {
                allowed: () => permissions()?.offers.canSee,
                title: 'title.create-offer',
                gtm: i18n.t('title.create-offer', GTM_LOCALE),
              },
            },
            {
              path: 'create',
              name: 'CreateOfferMotorbike',
              component: CreateOfferMotorbike,
              props: true,
              meta: {
                allowed: () => permissions()?.offers.canSee,
                title: 'title.create-offer',
                gtm: i18n.t('title.create-offer', GTM_LOCALE),
              },
            },
            {
              path: 'edit/:id',
              name: 'EditOfferMotorbike',
              component: CreateOfferMotorbike,
              meta: {
                allowed: () => permissions()?.offers.canSee,
                title: 'title.edit-offer',
                gtm: i18n.t('title.edit-offer', GTM_LOCALE),
              },
            },
            {
              path: 'resume/:id',
              name: 'ResumeOfferMotorbike',
              component: ResumeOfferMotorbike,
              props: true,
              meta: {
                allowed: () => permissions()?.offers.canSee,
                title: 'title.resume-offer',
                gtm: i18n.t('title.resume-offer', GTM_LOCALE),
              },
            },
          ],
        },
        {
          path: 'dealer-bikes',
          component: DealerBikes,
          meta: {allowed: () => permissions()?.dealerBikes.canSee},
          children: [
            {
              path: '',
              name: 'DealerBikesHome',
              component: DealerBikesList,
              meta: {
                allowed: () => permissions()?.dealerBikes.canSee,
                string: 'list',
                title: 'title.dealer-bike-list',
                gtm: i18n.t('title.dealer-bike-list', GTM_LOCALE),
              },
            },
            {
              path: 'list',
              name: 'DealerBikesList',
              component: DealerBikesList,
              meta: {
                allowed: () => permissions()?.dealerBikes.canSee,
                string: 'list',
                title: 'title.dealer-bike-list',
                gtm: i18n.t('title.dealer-bike-list', GTM_LOCALE),
              },
            },
            {
              path: 'registration',
              name: 'DealerBikesRegistration',
              component: DealerBikesRegistration,
              meta: {
                allowed: () => permissions()?.dealerBikes.canCreate,
                string: 'registration',
                canClose: true,
                title: 'title.dealer-bike-registration',
                gtm: i18n.t('title.dealer-bike-registration', GTM_LOCALE),
              },
            },
            {
              path: 'loans',
              name: 'LoansList',
              component: LoansList,
              meta: {
                allowed: () => permissions()?.dealerBikes.canSee,
                string: 'loans',
                title: 'title.dealer-bike-loans',
                gtm: i18n.t('title.dealer-bike-loans', GTM_LOCALE),
              },
            },
            {
              path: 'loans/:loanId/return',
              name: 'ReturnDealerBike',
              component: ReturnDealerBike,
              meta: {
                allowed: () => permissions()?.dealerBikes.canSee,
                string: 'return',
                canClose: true,
                redirectCloseName: 'LoansList',
                title: 'title.dealer-bike-loans',
                gtm: i18n.t('title.dealer-bike-loans', GTM_LOCALE),
              },
            },
            {
              path: ':dealerBikeId/loan',
              name: 'DealerBikesLoan',
              component: DealerBikesLoan,
              meta: {
                allowed: () => permissions()?.dealerBikes.canSee,
                string: 'loan',
                canClose: true,
                title: 'title.dealer-bike-loans',
                gtm: i18n.t('title.dealer-bike-loans', GTM_LOCALE),
              },
            },
            {
              path: ':dealerBikeId/detail',
              name: 'DealerBikeDetail',
              component: DealerBikeDetail,
              meta: {
                allowed: () => permissions()?.dealerBikes.canSee,
                string: 'detail',
                canClose: true,
                redirectCloseName: 'DealerBikesList',
                omitDialog: true,
                title: 'title.dealer-bike-detail',
                gtm: i18n.t('title.dealer-bike-detail', GTM_LOCALE),
              },
            },
          ],
        },
        {
          path: 'client-history/:id',
          name: 'ClientHistory',
          component: ClientHistory,
          meta: {
            allowed: () => permissions()?.clientHistory.canSee,
            canClose: true,
            string: 'client-history',
            title: 'title.client-history',
            gtm: i18n.t('title.client-history', GTM_LOCALE),
          },
        },
        {
          path: '*',
          redirect: '/',
        },
      ],
    },
  ],
})

// Development feature flag
// Persist debug query parameter across all navigation except login
if (isDevelopment()) {
  router.beforeEach((to, _from, next) => {
    // Remove debug parameter when navigating to login
    if (to.name === 'Login' && to.query.debug === 'true' && isDevelopment()) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {debug, ...queryWithoutDebug} = to.query
      next({
        path: to.path,
        query: queryWithoutDebug,
        params: to.params,
        hash: to.hash,
        replace: true,
      })
      return
    }

    // If current route has debug=true, preserve it in navigation (except to login)
    if (_from.query.debug === 'true' && to.query.debug !== 'true' && to.name !== 'Login') {
      next({
        path: to.path,
        query: {
          ...to.query,
          debug: 'true',
        },
        params: to.params,
        hash: to.hash,
        replace: true,
      })
      return
    }
    next()
  })
}

// Remove hash-mode artifacts when using history mode
router.beforeEach((to, _from, next) => {
  if (to.hash && to.hash.startsWith('#/')) {
    const pathFromHash = to.hash.substring(1)

    if (pathFromHash === '/') {
      return next({
        path: to.path,
        query: to.query,
        hash: '',
        replace: true,
      })
    }

    return next({
      path: pathFromHash,
      query: to.query,
      hash: '',
      replace: true,
    })
  }

  next()
})

router.beforeResolve(async (to, _from, next) => {
  if (to.matched.some(record => !record.meta.allowUnauthenticated)) {
    const user = await getUser()

    if (!user) {
      // Stop inactivity tracking when not authenticated
      const tracker = getInactivityTracker()
      if (tracker) {
        tracker.stop()
      }
      return next({name: 'Login', query: to.query})
    }

    await store.dispatch('loadDealer')

    // Start inactivity tracking when authenticated (Honda Cybersecurity Compliance requirement)
    const tracker = getInactivityTracker()
    if (tracker) {
      tracker.start()
    }

    const cognitoUser = store.getters.getCognitoUser

    // Enforce role-based access control (Honda Cybersecurity Compliance requirement)
    if (!hasAccess(user.dealerId, cognitoUser)) {
      console.log('🚨 Router guard - ACCESS DENIED, calling logout!')
      await store.dispatch('logout')
      store.dispatch('setAlertResult', {
        type: 'error',
        stringId: i18n.t('auth.noAccessPermissions'),
        open: true,
      })
      eventBus.$emit('access-denied')
      return next({name: 'Login', query: to.query})
    }

    if (store.getters.userHasNoDealer) {
      if (to.name === 'Ayuda' || to.name === 'Solicitudes') {
        return next()
      }

      if (to.name !== 'NoDealerId') {
        return next({name: 'NoDealerId'})
      }
      return next()
    }

    // UsersManager requires dealer whitelist check even for admins
    if (to.name === 'UsersManager' && !permissions()?.cognitoUserManagement?.canSee) {
      return next({name: 'Dashboard'})
    }

    // admin is allowed to visit all the pages
    if (store.getters.isAdmin) {
      return next()
    }

    // when you enter the Stock Manager page you are redirected to the Dashboard if you are a ServicePartner
    if (store.getters.isServicePartner && to.fullPath === '/stocks-manager') {
      return next({name: 'Dashboard'})
    }

    if (to.meta?.allowed && to.meta.allowed()) {
      return next()
    }

    // if not afected with any of back rules, redirect to dashboard
    if (to.name === 'Dashboard') {
      return next()
    } else {
      return next({name: 'Dashboard'})
    }
  }

  return next()
})

router.afterEach(async () => {
  const newVersion = await checkAppVersion()

  if (newVersion) {
    store.dispatch('setAppVersion', newVersion)
  }
})

export default router
